// src/prototype/components/sep/SEPSectionImplementationRoadmap.tsx

import React from 'react';
import { Zap, Layers, Eye, PauseCircle } from 'lucide-react';
import { isQuickWin } from '../../config/opportunityBadge';

interface Finalist {
  id: string;
  title: string;
  implementationEffort: 'Low' | 'Medium' | 'High';
  timeToValue: string;
  category: string;
  roiEstimate: { low: number; high: number; unit: string };
}

export interface SEPSectionImplementationRoadmapProps {
  finalists: Finalist[];
  clientName: string;
}

type BucketKey = 'launch' | 'pilot' | 'monitor' | 'defer';

interface Bucket {
  key: BucketKey;
  label: string;
  sublabel: string;
  icon: React.ReactNode;
  bg: string;
  border: string;
  labelColor: string;
}

const BUCKETS: Bucket[] = [
  { key: 'launch', label: 'Launch Now', sublabel: 'Ready for immediate execution', icon: <Zap className="w-4 h-4 text-emerald-600" />, bg: 'bg-emerald-50', border: 'border-emerald-100', labelColor: 'text-emerald-700' },
  { key: 'pilot', label: 'Stage First', sublabel: 'Structured first cohort before full rollout', icon: <Layers className="w-4 h-4 text-[#2563EB]" />, bg: 'bg-blue-50', border: 'border-blue-100', labelColor: 'text-blue-700' },
  { key: 'monitor', label: 'Monitor', sublabel: 'Watch and assess before committing', icon: <Eye className="w-4 h-4 text-amber-600" />, bg: 'bg-amber-50', border: 'border-amber-100', labelColor: 'text-amber-700' },
  { key: 'defer', label: 'Defer', sublabel: 'Not yet ready for this cycle', icon: <PauseCircle className="w-4 h-4 text-[#717182]" />, bg: 'bg-gray-50', border: 'border-gray-200', labelColor: 'text-[#717182]' },
];

// Launch-now is the Quick Win quadrant; everything else pilots first.
const getBucket = (f: Finalist): BucketKey =>
  isQuickWin({ id: f.id, implementationEffort: f.implementationEffort }) ? 'launch' : 'pilot';

const InitiativeChip: React.FC<{ finalist: Finalist; bucket: Bucket }> = ({ finalist, bucket }) => (
  <div className={`rounded-xl border p-3.5 ${bucket.bg} ${bucket.border}`}>
    <p className="text-xs font-semibold text-[#18365E] leading-snug mb-1.5">{finalist.title}</p>
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-[10px] text-[#717182]">{finalist.category}</span>
      <span className="text-[10px] font-semibold text-[#FF6E4C]">${finalist.roiEstimate.low}–{finalist.roiEstimate.high}{finalist.roiEstimate.unit}</span>
      <span className="text-[10px] text-[#717182]">{finalist.timeToValue}</span>
    </div>
  </div>
);

export const SEPSectionImplementationRoadmap: React.FC<SEPSectionImplementationRoadmapProps> = ({ finalists, clientName }) => {
  const byBucket: Record<BucketKey, Finalist[]> = { launch: [], pilot: [], monitor: [], defer: [] };
  finalists.forEach(f => byBucket[getBucket(f)].push(f));

  return (
    <div className="bg-white border border-[#e5e7eb] rounded-xl p-6 mb-6">
      <div className="flex items-start gap-3 mb-6">
        <div className="w-[3px] self-stretch rounded-full bg-[#FF6E4C] shrink-0 min-h-[40px]" />
        <div>
          <p className="text-[10px] font-bold text-[#FF6E4C] uppercase tracking-widest mb-0.5">Section 9</p>
          <h2 className="text-lg font-bold text-[#18365E] leading-tight">Implementation Roadmap</h2>
          <p className="text-[11px] text-[#717182] mt-0.5">Launch posture classification and recommended execution path per initiative</p>
        </div>
      </div>

      <p className="text-sm text-[#18365E]/80 leading-relaxed mb-6">
        Each initiative in <strong>{clientName}</strong>'s portfolio is classified by its recommended launch posture,
        reflecting organizational readiness, data maturity, and execution complexity. This roadmap guides immediate
        action prioritization and capacity planning across the full portfolio.
      </p>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {BUCKETS.map(bucket => (
          <div key={bucket.key} className="border border-[#e5e7eb] rounded-xl overflow-hidden">
            {/* Bucket header */}
            <div className={`flex items-center gap-2.5 px-4 py-3 ${bucket.bg} border-b ${bucket.border}`}>
              {bucket.icon}
              <div>
                <p className={`text-sm font-bold ${bucket.labelColor}`}>{bucket.label}</p>
                <p className="text-[10px] text-[#717182]">{bucket.sublabel}</p>
              </div>
              <div className="ml-auto">
                <span className={`text-xs font-bold ${bucket.labelColor}`}>{byBucket[bucket.key].length}</span>
              </div>
            </div>
            {/* Items */}
            <div className="p-3 space-y-2 bg-white min-h-[80px]">
              {byBucket[bucket.key].length > 0
                ? byBucket[bucket.key].map(f => <InitiativeChip key={f.id} finalist={f} bucket={bucket} />)
                : <p className="text-xs text-[#717182]/60 italic px-1 py-2">No initiatives in this bucket</p>
              }
            </div>
          </div>
        ))}
      </div>

      <div className="mt-5 bg-[#F5F7FA] border border-[#e5e7eb] rounded-xl p-4">
        <p className="text-[10px] font-semibold text-[#717182] uppercase tracking-wide mb-2">Roadmap Interpretation Guide</p>
        <div className="grid grid-cols-2 gap-x-6 gap-y-1.5">
          {BUCKETS.map(b => (
            <div key={b.key} className="flex items-start gap-2">
              <div className="mt-1 shrink-0">{b.icon}</div>
              <div>
                <p className={`text-[10px] font-semibold ${b.labelColor}`}>{b.label}</p>
                <p className="text-[10px] text-[#717182] leading-snug">{b.sublabel}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SEPSectionImplementationRoadmap;